import type { CheckboxProps } from '@mui/material'
import { TableRow } from '@mui/material'
import type { SortableHeaderProps } from '@app/src/components/Table'
import { TableCellWidth } from '@app/src/components/Table'
import TableHeaderCell from '@app/src/components/Table/Cells/TableHeaderCell'
import React from 'react'
import { useIntl } from 'react-intl'
import type { Question } from '@app/src/types/resourceExplorer'
import { QUESTIONNAIRES_COLUMN_WIDTH } from './QuestionnairesCell'
import type { QuestionCheckboxCellProps } from './QuestionCheckboxCell'
import { useQuestionCategories } from '@app/src/hooks/useQuestionCategories'

interface StandardQuestionHeaderProps extends SortableHeaderProps {
  includeCheckboxHeader: boolean
  selectionProps?: Partial<QuestionCheckboxCellProps>
  questionsInPage?: Question[]
}

const StandardQuestionHeader: React.FC<StandardQuestionHeaderProps> = ({
  toggleSorting,
  activeSorting,
  includeCheckboxHeader,
  selectionProps,
  questionsInPage,
}) => {
  const { formatMessage } = useIntl()
  const { selectedQuestionIds, setSelectedQuestionIds, questionIdsAlreadyInQuestionnaire } = selectionProps || {}
  const { basicCategories } = useQuestionCategories()

  const remainingQuestions = questionsInPage?.filter(
    question => !questionIdsAlreadyInQuestionnaire?.includes(question.id),
  )

  const isChecked = remainingQuestions?.every(question => selectedQuestionIds?.includes(question.id))

  const handleCheckboxChange: CheckboxProps['onChange'] = (_e, checked) => {
    if (checked) {
      setSelectedQuestionIds?.(prev =>
        Array.from(new Set([...prev, ...(remainingQuestions?.map(provider => provider.id) ?? [])])),
      )
    } else {
      setSelectedQuestionIds?.([])
    }
  }

  return (
    <TableRow>
      {includeCheckboxHeader && (
        <TableHeaderCell
          label=""
          minWidth={TableCellWidth.EMPTY}
          checkbox={{ checked: isChecked, onChange: handleCheckboxChange, color: 'primary' }}
        />
      )}
      <TableHeaderCell label={formatMessage({ id: 'general.id' })} minWidth={TableCellWidth.EXTRA_SMALL} />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.question.name' })}
        sorting={{ name: 'questionText', toggleSorting, activeSorting }}
        minWidth={TableCellWidth.LARGE}
      />
      <TableHeaderCell label={formatMessage({ id: 'schemas.question.guidance' })} minWidth={TableCellWidth.SMALL} />
      {basicCategories.map(category => (
        <TableHeaderCell key={category.id} label={category.name} minWidth={TableCellWidth.SMALL} />
      ))}
      {includeCheckboxHeader && (
        <TableHeaderCell
          label={formatMessage({ id: 'schemas.question.questionnaires' })}
          minWidth={QUESTIONNAIRES_COLUMN_WIDTH}
        />
      )}
    </TableRow>
  )
}

export default StandardQuestionHeader
